import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@cyphex/ui';
import type { CustomEmoji } from '@cyphex/types/admin';
import { ApiRequestError } from '@/lib/api/client';
import { emojiApi, type CreateEmojiInput } from '../api/emojiApi';
import { EMOJI_KEYS } from '../api/queryKeys';

/** The server's message is the useful one for quota and duplicate-name errors. */
function messageFor(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) {
    if (error.code === 'already_exists') return 'An emoji with that name already exists.';
    if (error.code === 'quota_exceeded' || error.code === 'bad_request') return error.message;
  }
  return fallback;
}

export interface CreateEmojiResult {
  create: (input: CreateEmojiInput) => void;
  isCreating: boolean;
}

export function useCreateEmoji(onCreated?: (emoji: CustomEmoji) => void): CreateEmojiResult {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (input: CreateEmojiInput) => emojiApi.create(input),
    onSuccess: (emoji) => {
      void queryClient.invalidateQueries({ queryKey: EMOJI_KEYS.list() });
      toast.success(`:${emoji.name}: is ready to use.`);
      onCreated?.(emoji);
    },
    onError: (error) => toast.error(messageFor(error, 'Could not add that emoji.')),
  });

  return { create: mutation.mutate, isCreating: mutation.isPending };
}

export interface DeleteEmojiResult {
  remove: (emojiId: string) => void;
  isRemoving: boolean;
}

export function useDeleteEmoji(): DeleteEmojiResult {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (emojiId: string) => emojiApi.remove(emojiId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: EMOJI_KEYS.list() });
      toast.success('Emoji removed.');
    },
    onError: (error) =>
      toast.error(messageFor(error, 'Could not remove that emoji.')),
  });

  return { remove: mutation.mutate, isRemoving: mutation.isPending };
}
